import type { PlanV2IssueDispositionRecord } from './plan-v2-issues';

/**
 * Which technical checks arrive unticked on the next page.
 *
 * The review surface ticks everything by default and the operator unticks the
 * exceptions. That is right on page 1 and wrong by page 5: a reviewer who has
 * decided they are not fixing "Images missing title attributes" has to untick
 * it forty-four more times, and the checks they DO care about have to compete
 * with forty-four repeats of the one they don't.
 *
 * So an untick is remembered against the CHECK, for the period, and the next
 * page renders that check already unticked — visible, and one click from being
 * ticked again. Ticking it again forgets the mute.
 *
 * **This is not suppression, and the difference is the whole design.** Two
 * things can happen to a check across a review, and they must not be confused:
 *
 * - **Muted** (here) — you said "not this one". The check keeps appearing,
 *   unticked, because "not on that page" is not "not ever" and you have to be
 *   able to change your mind on the page where it matters.
 * - **Settled** (`utils/site-wide-suppression`) — you SENT a site-wide defect.
 *   It is one template fix that clears the finding on every page, so asking
 *   again is asking you to queue the same work twice. It stops appearing.
 *
 * Scope does not enter into muting. A reviewer who does not want page titles
 * shortened does not want them shortened on page 2 either, and whether the
 * crawler happened to flag the title on one page or three hundred is a fact
 * about the site, not about the decision.
 */

const MUTED_KEY_PREFIX = 'muted::';

/** The disposition key for one muted check. */
export function mutedCheckKey(code: string): string {
  return `${MUTED_KEY_PREFIX}${(code || '').trim()}`;
}

/** The code back out of a key, or '' when the key is not one of ours. */
export function mutedCheckCodeFromKey(issueKey: string): string {
  const key = (issueKey || '').trim();
  return key.startsWith(MUTED_KEY_PREFIX) ? key.slice(MUTED_KEY_PREFIX.length) : '';
}

/**
 * Checks the operator has unticked this period.
 *
 * Read from the queue service's records rather than counted locally, so a mute
 * set in an earlier session — or in another tab — still holds. Queue is not
 * checked because a mute row only ever exists in one state: the write that
 * un-mutes deletes the row rather than flipping it, so a row's presence IS the
 * mute.
 */
export function mutedCheckCodes(
  records: Iterable<PlanV2IssueDispositionRecord> | null | undefined
): Set<string> {
  const codes = new Set<string>();
  for (const record of records ?? []) {
    if (!record || record.issueType !== 'check_muted') continue;
    const code = mutedCheckCodeFromKey(record.issueKey);
    if (code) codes.add(code);
  }
  return codes;
}
